import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import type { CallBackProps } from 'react-joyride';
import { useAuthStore } from './authStore';
import { TOUR_ROUTES, isTourAllowed } from '../tours/access';
import {
  AVAILABLE_WORKFLOW_TOURS,
  resolveTourForPath,
  type AnyTourName,
  type ModalTourName,
  type TourName,
  type TourProgress,
  type TourState,
  type WorkflowTourName,
} from '../tours/types';

export const AVAILABLE_TOURS: TourName[] = [
  'products',
  'wildberries',
  'ozon',
  'designs',
  'categories',
  'designer-create',
  'designer-stats',
  'designer-payouts',
  'designer-tariffs',
  'layout-jobs',
  'layout-create',
  'layout-studio',
  'layout-job',
  'layout-duplicate',
  'layout-help',
];

export const AVAILABLE_MODAL_TOURS: ModalTourName[] = [
  'product-create',
  'product-edit',
  'design-create',
  'design-edit',
  'design-import',
  'designer-theme-create',
  'designer-theme-new',
  'designer-theme-task',
  'designer-theme-upload',
  'designer-pdf-upload',
];

function accessContext() {
  const user = useAuthStore.getState().user;
  return { role: user?.role, permissions: user?.permissions };
}

function canStart(name: AnyTourName): boolean {
  return isTourAllowed(name, accessContext());
}

function emptyProgress(): Partial<Record<AnyTourName, TourProgress>> {
  return {};
}

function sanitizeProgress(value: unknown): Partial<Record<AnyTourName, TourProgress>> {
  if (!value || typeof value !== 'object') return emptyProgress();
  const result = emptyProgress();
  for (const [name, raw] of Object.entries(value)) {
    if (!(name in TOUR_ROUTES) || !raw || typeof raw !== 'object') continue;
    const progress = raw as Partial<TourProgress>;
    if (typeof progress.completed !== 'boolean' || typeof progress.lastStep !== 'number') continue;
    const tourName = name as AnyTourName;
    result[tourName] = {
      tourName,
      completed: progress.completed,
      lastStep: Math.max(0, progress.lastStep),
      ...(typeof progress.completedAt === 'string' ? { completedAt: progress.completedAt } : {}),
      ...(typeof progress.skippedAt === 'string' ? { skippedAt: progress.skippedAt } : {}),
    };
  }
  return result;
}

export const useTourStore = create<TourState>()(
  persist(
    (set, get) => ({
      activeTour: null,
      currentStepIndex: 0,
      isRunning: false,
      tourProgress: emptyProgress(),

      startTour: (tourName) => {
        if (!AVAILABLE_TOURS.includes(tourName) || !canStart(tourName)) {
          console.warn(`[TourStore] Тур "${tourName}" недоступен`);
          return;
        }
        set({ activeTour: tourName, currentStepIndex: 0, isRunning: true });
      },

      endTour: () => {
        const { activeTour, currentStepIndex } = get();
        if (activeTour) {
          set((state) => ({
            tourProgress: {
              ...state.tourProgress,
              [activeTour]: {
                tourName: activeTour,
                completed: true,
                lastStep: currentStepIndex,
                completedAt: new Date().toISOString(),
              },
            },
          }));
        }
        set({ activeTour: null, currentStepIndex: 0, isRunning: false });
      },

      nextStep: () => set((state) => ({ currentStepIndex: state.currentStepIndex + 1 })),
      prevStep: () => set((state) => ({ currentStepIndex: Math.max(0, state.currentStepIndex - 1) })),
      goToStep: (currentStepIndex) => set({ currentStepIndex }),

      skipTour: () => {
        const { activeTour, currentStepIndex } = get();
        if (activeTour) {
          set((state) => ({
            tourProgress: {
              ...state.tourProgress,
              [activeTour]: {
                tourName: activeTour,
                completed: false,
                lastStep: currentStepIndex,
                skippedAt: new Date().toISOString(),
              },
            },
          }));
        }
        set({ activeTour: null, currentStepIndex: 0, isRunning: false });
      },

      resetTour: (tourName) => set((state) => {
        const tourProgress = { ...state.tourProgress };
        delete tourProgress[tourName];
        return { tourProgress };
      }),
      resetAllTours: () => set({ tourProgress: emptyProgress() }),

      handleJoyrideCallback: (data: CallBackProps) => {
        const { action, index, status, type } = data;
        if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
          get().skipTour();
          return;
        }
        if (status === STATUS.FINISHED) {
          get().endTour();
          return;
        }
        if (status === STATUS.ERROR) {
          get().nextStep();
          return;
        }
        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
          if (action === ACTIONS.NEXT) set({ currentStepIndex: index + 1 });
          if (action === ACTIONS.PREV) set((state) => ({ currentStepIndex: Math.max(0, state.currentStepIndex - 1) }));
        }
      },

      isTourCompleted: (tourName) => get().tourProgress[tourName]?.completed ?? false,
      hasTourForPage: (path) => {
        const tourName = resolveTourForPath(path);
        return Boolean(tourName && AVAILABLE_TOURS.includes(tourName) && canStart(tourName));
      },
      getTourForPage: (path) => {
        const tourName = resolveTourForPath(path);
        return tourName && AVAILABLE_TOURS.includes(tourName) && canStart(tourName) ? tourName : null;
      },

      startModalTour: (modalName) => {
        if (!AVAILABLE_MODAL_TOURS.includes(modalName) || !canStart(modalName)) {
          console.warn(`[TourStore] Модальный тур "${modalName}" недоступен`);
          return;
        }
        set({ activeTour: modalName, currentStepIndex: 0, isRunning: true });
      },
      shouldShowModalTour: (modalName) =>
        AVAILABLE_MODAL_TOURS.includes(modalName)
        && canStart(modalName)
        && !get().tourProgress[modalName]?.completed,

      startWorkflowTour: (workflowName) => {
        if (!AVAILABLE_WORKFLOW_TOURS.includes(workflowName) || !canStart(workflowName)) {
          console.warn(`[TourStore] Workflow-тур "${workflowName}" недоступен`);
          return;
        }
        set({ activeTour: workflowName, currentStepIndex: 0, isRunning: true });
      },
      isWorkflowTourCompleted: (workflowName: WorkflowTourName) =>
        get().tourProgress[workflowName]?.completed ?? false,
    }),
    {
      name: 'pim-tour-storage',
      version: 2,
      partialize: (state) => ({ tourProgress: state.tourProgress }),
      migrate: (persistedState) => {
        const state = persistedState && typeof persistedState === 'object'
          ? persistedState as { tourProgress?: unknown }
          : {};
        return { tourProgress: sanitizeProgress(state.tourProgress) };
      },
    },
  ),
);
